/**
 * Queue page — view, drag-reorder, jump-to-track with live ETAs.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trash2, Play, GripVertical } from "lucide-react";
import type { Track } from "@memes/shared";
import { isOwnerActive, usePlayer } from "../state/player";
import { countPlayable, countSkipped } from "../lib/shuffleActiveOwners";
import { CoverImg } from "../components/CoverImg";
import { LoudnessIndicator } from "../components/LoudnessIndicator";
import { OwnerBadge } from "../components/OwnerBadge";
import { fmtDuration } from "../lib/format";
import { formatQueueEta } from "../lib/queueEta";
import { useClearQueue, usePutQueue } from "../hooks/useQueue";

interface SortableRowProps {
  track: Track;
  index: number;
  currentIndex: number;
  currentTime: number;
  isPlaying: boolean;
  activeOwners: ReturnType<typeof usePlayer.getState>["activeOwners"];
  queue: Track[];
  onPlay: (i: number) => void;
}

const SortableQueueRow: React.FC<SortableRowProps> = ({
  track,
  index,
  currentIndex,
  currentTime,
  isPlaying,
  activeOwners,
  queue,
  onPlay,
}) => {
  const skippedRow = !isOwnerActive(activeOwners, track.owner);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: `${track.id}-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 ${
        index === currentIndex ? "bg-indigo-50 dark:bg-indigo-950" : ""
      } ${skippedRow ? "opacity-50" : ""} ${isDragging ? "z-10 shadow-lg ring-2 ring-indigo-400" : ""}`}
    >
      <button
        type="button"
        className="btn cursor-grab touch-none p-1 active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </button>
      <span className="w-6 text-right text-xs text-gray-500">{index + 1}</span>
      <CoverImg track={track} className="h-10 w-10" rounded="rounded-md" />
      <div className="min-w-0 flex-1">
        <div
          className={`truncate text-sm font-medium ${skippedRow ? "line-through" : ""}`}
        >
          {track.metadataTitle ?? track.name}
        </div>
        <div className="truncate text-xs text-gray-500">
          {track.metadataArtist ?? "Unknown artist"}
        </div>
      </div>
      <OwnerBadge owner={track.owner} />
      <LoudnessIndicator track={track} />
      <span className="w-14 text-right text-xs text-gray-500" title="Track length">
        {fmtDuration(track.durationSec)}
      </span>
      <span
        className={`w-16 text-right text-xs font-medium tabular-nums ${
          index === currentIndex ? "text-indigo-600 dark:text-indigo-300" : "text-gray-500"
        }`}
        title="Time until this track plays"
      >
        {formatQueueEta(index, queue, currentIndex, currentTime, isPlaying, activeOwners)}
      </span>
      <button
        className="btn btn-primary"
        onClick={() => onPlay(index)}
        disabled={skippedRow}
        aria-label={`Play ${track.name}`}
      >
        <Play className="h-4 w-4" />
      </button>
    </li>
  );
};

export const Queue: React.FC = () => {
  const navigate = useNavigate();
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const currentTime = usePlayer((s) => s.currentTime);
  const isPlaying = usePlayer((s) => s.isPlaying);
  const play = usePlayer((s) => s.play);
  const reorderQueueLocal = usePlayer((s) => s.reorderQueueLocal);
  const activeOwners = usePlayer((s) => s.activeOwners);
  const clear = useClearQueue();
  const putQueue = usePutQueue();

  const [, tick] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const pushQueueToServer = useCallback(() => {
    const { queue: q, index: idx, currentTime: ct } = usePlayer.getState();
    if (q.length === 0) return;
    void putQueue.mutateAsync({
      queue: q.map((t) => ({ id: t.id, durationSec: t.durationSec ?? undefined })),
      currentIndex: idx,
      currentTime: ct,
    });
  }, [putQueue]);

  useEffect(() => {
    document.title = "Queue · Memes & Shit";
  }, []);

  useEffect(() => {
    if (!isPlaying || queue.length === 0) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isPlaying, queue.length]);

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = queue.findIndex((t, i) => `${t.id}-${i}` === active.id);
    const newIndex = queue.findIndex((t, i) => `${t.id}-${i}` === over.id);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    reorderQueueLocal(oldIndex, newIndex);
    pushQueueToServer();
  };

  if (queue.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-sm text-gray-500">No queue yet.</p>
        <button className="btn btn-primary mt-4" onClick={() => navigate("/")}>
          Build a shuffle
        </button>
      </div>
    );
  }

  const total = queue.reduce((acc, t) => acc + (t.durationSec ?? 0), 0);
  const playable = countPlayable(queue, activeOwners);
  const skipped = countSkipped(queue, activeOwners);
  const sortableIds = queue.map((t, i) => `${t.id}-${i}`);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Queue</h1>
          <p className="text-sm text-gray-500">
            {playable} active / {queue.length} total
            {skipped > 0 ? `, ${skipped} skipped` : ""} · {fmtDuration(total)}
          </p>
          <p className="text-xs text-gray-400">Drag rows to reorder</p>
        </div>
        <button
          className="btn btn-danger"
          onClick={() => {
            if (confirm("Clear the entire queue?")) void clear.mutateAsync();
          }}
        >
          <Trash2 className="h-4 w-4" /> Clear queue
        </button>
      </header>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <ul className="divide-y rounded-2xl border bg-white shadow-sm dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
            {queue.map((t, i) => (
              <SortableQueueRow
                key={`${t.id}-${i}`}
                track={t}
                index={i}
                currentIndex={index}
                currentTime={currentTime}
                isPlaying={isPlaying}
                activeOwners={activeOwners}
                queue={queue}
                onPlay={play}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};
